// apps/client/src/world/terrain/textures.ts — the look demo's canvas textures as DataTextures, so terrain builds without a canvas (Node tests)
import { rngFrom } from '@whiskerton/shared';
import * as THREE from 'three';

const SIZE = 128;
function data(pixels: Uint8Array, colorSpace: THREE.ColorSpace): THREE.DataTexture {
  const t = new THREE.DataTexture(pixels, SIZE, SIZE);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.colorSpace = colorSpace;
  t.anisotropy = 4;
  t.needsUpdate = true;
  return t;
}
const meadows = new Map<string, THREE.DataTexture>();
/** Soft tonal blotches so a big lawn never reads as one flat colour (look demo); tiles seamlessly, one repeat per 26 tiles. */
export function meadowTexture(base: string): THREE.DataTexture {
  const cached = meadows.get(base);
  if (cached) return cached;
  const rng = rngFrom('meadow', base);
  const hsl = { h: 0, s: 0, l: 0 };
  new THREE.Color(base).getHSL(hsl, THREE.SRGBColorSpace);
  const light = new Float32Array(SIZE * SIZE);                 // lightness offset per pixel, blotches wrap around the edges
  for (let i = 0; i < 60; i++) {
    const cx = rng.next() * SIZE, cy = rng.next() * SIZE, r = 6 + rng.next() * 20, sign = rng.chance(0.5) ? 1 : -1;
    const x0 = Math.floor(cx - r), x1 = Math.ceil(cx + r), y0 = Math.floor(cy - r), y1 = Math.ceil(cy + r);
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
      const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy) / r;
      if (d >= 1) continue;
      const k = (1 - d * d) ** 2;
      const px = ((x % SIZE) + SIZE) % SIZE, py = ((y % SIZE) + SIZE) % SIZE;
      light[py * SIZE + px]! += sign * 0.08 * k;
    }
  }
  const pixels = new Uint8Array(SIZE * SIZE * 4), out = new THREE.Color(), rgb = { r: 0, g: 0, b: 0 };
  const byte = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255);
  for (let i = 0; i < SIZE * SIZE; i++) {
    out.setHSL(hsl.h, hsl.s, Math.min(1, hsl.l * (1 + Math.max(-0.16, Math.min(0.16, light[i]!)))), THREE.SRGBColorSpace);
    out.getRGB(rgb, THREE.SRGBColorSpace);
    pixels.set([byte(rgb.r), byte(rgb.g), byte(rgb.b), 255], i * 4);
  }
  const t = data(pixels, THREE.SRGBColorSpace);
  t.repeat.set(1 / 26, 1 / 26);
  meadows.set(base, t);
  return t;
}

let ripples: THREE.DataTexture | null = null;
/** The look demo's ripple normal map (two octaves of value noise, finite differences), periodic so it tiles; repeat 30 on the sea plane. */
export function ripplesTexture(): THREE.DataTexture {
  if (ripples) return ripples;
  const rng = rngFrom('ripples');
  const octave = (cells: number) => {
    const g = new Float32Array(cells * cells);
    for (let i = 0; i < g.length; i++) g[i] = rng.next();
    const at = (x: number, y: number) => g[(((y % cells) + cells) % cells) * cells + (((x % cells) + cells) % cells)]!;
    return (u: number, v: number) => {                         // u, v in pixels; periodic over SIZE
      const fx = (u / SIZE) * cells, fy = (v / SIZE) * cells, ix = Math.floor(fx), iy = Math.floor(fy);
      const tx = fx - ix, ty = fy - iy, sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
      const a = at(ix, iy), b = at(ix + 1, iy), c = at(ix, iy + 1), d = at(ix + 1, iy + 1);
      return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
    };
  };
  const o1 = octave(12), o2 = octave(29);
  const h = (u: number, v: number) => o1(u, v) * 0.6 + o2(u, v) * 0.4;
  const pixels = new Uint8Array(SIZE * SIZE * 4);
  for (let v = 0; v < SIZE; v++) for (let u = 0; u < SIZE; u++) {
    const dx = h(u + 1, v) - h(u - 1, v), dy = h(u, v + 1) - h(u, v - 1);
    const nx = -dx * 6, ny = -dy * 6, nz = 1, l = Math.hypot(nx, ny, nz);
    pixels.set([(nx / l * 0.5 + 0.5) * 255, (ny / l * 0.5 + 0.5) * 255, (nz / l * 0.5 + 0.5) * 255, 255].map(Math.round), (v * SIZE + u) * 4);
  }
  ripples = data(pixels, THREE.NoColorSpace);
  ripples.repeat.set(30, 30);
  return ripples;
}
